import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  startPKCEFlow,
  refreshAccessToken,
  fetchUserInfo,
  setPrimaryLanguage,
} from '../services/SSO';
import SectionLoadingIndicator from '../components/molecules/SectionLoadingIndicator';
import Logo from '../components/atoms/Logo';
import { userDetailSegment } from '../store/globalStore/stateSegments/userDetailSegment';
import store from '../store/markettingContentGeneration';
import { encodeToBase64 } from '../utils/encodeString';
import { ListUserType } from '../types/Users';
import { initializeFeatureFlags } from '../store/globalStore/actions/featureFlagsActions';

interface SSOProviderProps {
  children: React.ReactNode;
}

const testTime = localStorage.getItem('refresh-token-time');

const FIFTY_FIVE_MINUTES = testTime ? parseInt(testTime) : 1000 * 60 * 55;

export const SSOProvider: React.FC<SSOProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Updates the user detail segment in the store with the client ID and its Base64 encoded value.
   *
   * @param {ListUserType | null} userInfo - The user information object containing the client ID.
   */
  function updateUserDetails(userInfo: ListUserType | null): void {
    if (userInfo) {
      const { clientId } = userInfo;
      store.set(userDetailSegment, prev => ({
        ...prev,
        userNtid: clientId,
        userNtidEncoded: encodeToBase64(clientId) ?? '',
      }));
    }
  }

  // Fixed function to handle automatic token refresh with proper recursion
  const scheduleTokenRefresh = useCallback(() => {
    // Clear existing timer to prevent multiple timers
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const refreshToken = sessionStorage.getItem('pfz-cggenai-token-refresh');
    const expiresAt = sessionStorage.getItem('pfz-cggenai-token-expiry');

    if (!refreshToken) {
      // console.warn('[SSO] No refresh token available');
      return;
    }

    // Calculate time until token expires (with 5min buffer)
    const BUFFER_TIME = 5 * 60 * 1000; // 5 minutes
    const expiryTime = expiresAt
      ? parseInt(expiresAt)
      : Date.now() + FIFTY_FIVE_MINUTES;
    const timeUntilRefresh = Math.max(0, expiryTime - Date.now() - BUFFER_TIME);

    // console.log(
    //   `[SSO] Token refresh scheduled in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`
    // );

    refreshTimerRef.current = setTimeout(async () => {
      try {
        // console.log('[SSO] Attempting automatic token refresh...');
        await refreshAccessToken(refreshToken);
        // console.log('[SSO] Token refreshed successfully');
        setIsLoggedIn(true);

        // Schedule next refresh after successful refresh
        scheduleTokenRefresh();
      } catch {
        // console.error('[SSO] Token refresh failed:', error);
        // Let axios interceptor handle the 401, don't redirect here
      }
    }, timeUntilRefresh);
  }, []);

  useEffect(() => {
    if (import.meta.env.MODE === 'development') {
      setIsLoggedIn(true);
      setPrimaryLanguage('en');
      (async () => {
        const userInfo = await fetchUserInfo();
        updateUserDetails(userInfo);
        await initializeFeatureFlags();
      })();
      return; // Skip SSO in development
    }

    const init = async () => {
      const accessToken = sessionStorage.getItem('pfz-cggenai-token-access');
      const refreshToken = sessionStorage.getItem('pfz-cggenai-token-refresh');
      const expiresAt = sessionStorage.getItem('pfz-cggenai-token-expiry');

      if (!accessToken && !refreshToken) {
        await startPKCEFlow();
        return; // Don't schedule refresh if no tokens
      }

      if (accessToken && Date.now() > parseInt(expiresAt || '0')) {
        try {
          await refreshAccessToken(refreshToken || '');
          setIsLoggedIn(true);
        } catch {
          await startPKCEFlow();
          return; // Don't schedule refresh if initial refresh fails
        }
      } else {
        const userInfo = await fetchUserInfo();
        updateUserDetails(userInfo);
        setIsLoggedIn(true);
      }

      // Initialize feature flags at boot time
      await initializeFeatureFlags();

      // Schedule automatic token refresh after successful initialization
      scheduleTokenRefresh();
    };

    init();

    // Cleanup timer on component unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        // console.log('[SSO] Token refresh timer cleaned up');
      }
    };
  }, [scheduleTokenRefresh]);

  return isLoggedIn ? (
    <div>{children}</div>
  ) : (
    <div className='preloader_wrapper'>
      <Logo />
      <SectionLoadingIndicator text='Getting things ready...' />
    </div>
  );
};
SSOProvider.displayName = 'SSOProvider';
